import type { HiredEquipmentEntryMethod } from '../types';
import { HIRED_EQUIPMENT_ENTRY_METHODS } from '../types';
import { methodLabel } from './buildAnchor';
import { formatDate as formatPacificDate } from '@/utils/displayDateTime';

export function formatInitialLastName(name?: string | null, lastName?: string | null): string {
  const first = (name || '').trim();
  const last = (lastName || '').trim();
  if (!first && !last) return '';
  if (!first) return last;
  return last ? `${first.charAt(0).toUpperCase()}. ${last}` : first;
}

/** "16 — 100 Mile" / "16" → "SA 16"; otherwise the raw label. */
export function formatHeServiceAreaLabel(raw?: string | null): string {
  const value = (raw || '').trim();
  if (!value) return '';
  const match = value.match(/^SA\s*(\d{1,3})\b/i) || value.match(/^(\d{1,3})\b/);
  if (match) return `SA ${match[1]}`;
  return value;
}

export function formatHeWorkDate(raw?: string | null): string {
  const value = (raw || '').trim();
  if (!value) return '';
  // V2-073: YYYY-MM-DD; a `YYYY-MM-DD…` work date is a calendar date and is not shifted.
  const ymd = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return formatPacificDate(ymd ? ymd[1] : value) || value;
}

export function heVendorNameFromForm(
  fields: Array<{ field: string; path?: string; value?: string | null }>,
): string {
  const setup = fields.find(
    (f) => f.field === 'SubcontractorNameCom' && !(f.path || '').includes('WorkCompleted'),
  )?.value?.trim();
  if (setup) return setup;
  return fields.find((f) => f.field === 'SubcontractorNameCom')?.value?.trim() || '';
}

/** C6: the setup ForemanArea text written at submit (the work's foreman area). */
export function heForemanAreaFromForm(
  fields: Array<{ field: string; path?: string; value?: string | null }>,
): string {
  return (
    fields.find((f) => f.field === 'ForemanArea' && !(f.path || '').includes('WorkCompleted'))?.value?.trim() || ''
  );
}

export function heServiceAreaFromForm(
  fields: Array<{ field: string; value?: string | null }>,
): string {
  return fields.find((f) => f.field === 'ServiceArea')?.value?.trim() || '';
}

/**
 * C6a: the work area to show on a Hired Equipment row and in its expanded view. The text written
 * at submit wins; older forms fall back to the foreman area stored on the form (from its task).
 */
export function heWorkAreaLabels(form: {
  fields: Array<{ field: string; path?: string; value?: string | null }>;
  homeForemanArea?: { name?: string | null; serviceArea?: { name?: string | null; code?: string | null } | null } | null;
}): { foremanArea: string; serviceArea: string } {
  const stored = form.homeForemanArea;
  const storedSa = stored?.serviceArea;
  const storedSaLabel = storedSa?.code?.trim()
    ? `${storedSa.code.trim()}${storedSa.name ? ` – ${storedSa.name}` : ''}`
    : storedSa?.name?.trim() || '';
  return {
    foremanArea: heForemanAreaFromForm(form.fields) || stored?.name?.trim() || '',
    serviceArea: heServiceAreaFromForm(form.fields) || storedSaLabel,
  };
}

/**
 * V2-048: the submission's ticket number (e.g. HE1626-00004) – the HiredEquipmentTicket field,
 * else the ticket in the form name ("Hired Equipment HE1626-00004"). Empty for a preview.
 */
export function heTicketFromForm(form: {
  name?: string | null;
  fields: Array<{ field: string; value?: string | null }>;
}): string {
  const stored = form.fields.find((f) => f.field === 'HiredEquipmentTicket')?.value?.trim();
  if (stored) return stored;
  return form.name?.match(/\bHE\d+-\d+\b/)?.[0] ?? '';
}

/** Setup PO first (purchase-order submissions), then any line-level PONumber. */
export function hePoNumberFromForm(
  fields: Array<{ field: string; path?: string; value?: string | null }>,
): string {
  const setup = fields.find(
    (f) => f.field === 'PONumber' && !(f.path || '').includes('WorkCompleted'),
  )?.value?.trim();
  if (setup) return setup;
  return fields.find((f) => f.field === 'PONumber')?.value?.trim() || '';
}

/** Prefer a line work date; fall back to the setup ActualStartTime. */
export function heWorkDateFromForm(
  fields: Array<{ field: string; path?: string; value?: string | null }>,
): string {
  const lineDate = fields.find(
    (f) => f.field === 'ActualStartTime' && (f.path || '').includes('WorkCompleted'),
  )?.value?.trim();
  if (lineDate) return lineDate;
  return fields.find((f) => f.field === 'ActualStartTime')?.value?.trim() || '';
}

export function heEntryMethodFromForm(fields: Array<{ field: string; value?: string | null }>): HiredEquipmentEntryMethod | null {
  const stored = fields.find((f) => f.field === 'EntryMethod')?.value?.trim();
  if (stored && (HIRED_EQUIPMENT_ENTRY_METHODS as readonly string[]).includes(stored)) {
    return stored as HiredEquipmentEntryMethod;
  }
  if (fields.some((f) => f.field === 'PONumber' && f.value?.trim())) return 'purchase_order';
  if (fields.some((f) => f.field === 'SubcontractorOrHERep' && f.value?.trim() === 'Subcontractor')) {
    return 'subcontractor';
  }
  return null;
}

export function heApprovalTitleParts(input: {
  vendorName?: string | null;
  submitterName?: string | null;
  submitterLastName?: string | null;
  roleName?: string | null;
  total: number;
}): { lead: string; cost: string } {
  const vendor = input.vendorName?.trim() || 'Hired Equipment';
  const person = formatInitialLastName(input.submitterName, input.submitterLastName);
  const role = input.roleName?.trim();
  const who = person ? (role ? `${person} (${role})` : person) : '';
  const cost = `$${input.total.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  return { lead: who ? `${vendor} – ${who}` : vendor, cost };
}

export function formatHeApprovalTitle(input: {
  vendorName?: string | null;
  submitterName?: string | null;
  submitterLastName?: string | null;
  roleName?: string | null;
  total: number;
}): string {
  const { lead, cost } = heApprovalTitleParts(input);
  return `${lead} · ${cost}`;
}

export function formatHeApprovalSubtitle(input: {
  /** V2-048: leads the subtitle, so each card shows its ticket number. */
  ticketNumber?: string | null;
  entryMethod?: HiredEquipmentEntryMethod | null;
  poNumber?: string | null;
  serviceAreaLabel?: string | null;
  foremanAreaLabel?: string | null;
  workDate?: string | null;
}): string {
  const parts: string[] = [];
  const ticket = input.ticketNumber?.trim();
  if (ticket) parts.push(ticket);
  if (input.entryMethod) parts.push(methodLabel(input.entryMethod));
  const po = input.poNumber?.trim();
  if (po) parts.push(po);
  const sa = formatHeServiceAreaLabel(input.serviceAreaLabel);
  if (sa) parts.push(sa);
  const fa = input.foremanAreaLabel?.trim();
  if (fa) parts.push(fa);
  const date = formatHeWorkDate(input.workDate);
  if (date) parts.push(date);
  return parts.join(' | ');
}
